import type { ControlLabel, FormatLabel, TrelloLabel } from '@/types'

/**
 * Normalises a string for comparison:
 * - Strips accents (NFD decomposition + remove combining marks)
 * - Lowercases
 * - Trims whitespace
 */
export function normalise(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

// ─── Format label map ────────────────────────────────────────────────────────

const FORMAT_MAP: Record<string, FormatLabel> = {
  carrossel: 'carrossel',
  carousel: 'carrossel',
  reel: 'reel',
  reels: 'reel',
  tiktok: 'tiktok',
  'tik tok': 'tiktok',
  story: 'story',
  stories: 'story',
  linkedin: 'linkedin',
}

// ─── Control label map ───────────────────────────────────────────────────────

const CONTROL_MAP: Record<string, ControlLabel> = {
  'aguardando material': 'aguardando-material',
  'aguardando materiais': 'aguardando-material',
  'ag. material': 'aguardando-material',
  gravar: 'gravar',
  gravacao: 'gravar',
  gravação: 'gravar',
  design: 'design',
  video: 'video',
  vídeo: 'video',
  finalizado: 'finalizado',
  finalizada: 'finalizado',
  agendado: 'agendado',
  agendada: 'agendado',
  'postar manualmente': 'postar-manualmente',
  revisao: 'revisao',
  revisão: 'revisao',
}

export const CONTROL_DISPLAY_NAMES: Record<ControlLabel, string> = {
  'aguardando-material': 'Aguardando material',
  gravar: 'Gravar',
  video: 'Vídeo',
  design: 'Design',
  finalizado: 'Finalizado',
  agendado: 'Agendado',
  'postar-manualmente': 'Postar manualmente',
  revisao: 'Revisão',
  outros: 'Outros',
  'sem-etiqueta': 'Sem etiqueta de controle',
}

export const FORMAT_DISPLAY_NAMES: Record<FormatLabel, string> = {
  carrossel: 'Carrossel',
  reel: 'Reel',
  tiktok: 'TikTok',
  story: 'Story',
  linkedin: 'LinkedIn',
  'outro-formato': 'Outro formato',
}

// ─── Parsers ─────────────────────────────────────────────────────────────────

export function parseFormatLabel(name: string): FormatLabel | null {
  const key = normalise(name)
  return FORMAT_MAP[key] ?? null
}

export function parseControlLabel(name: string): ControlLabel | null {
  const key = normalise(name)
  return CONTROL_MAP[key] ?? null
}

export function classifyLabels(labels: TrelloLabel[]): {
  formatLabels: FormatLabel[]
  controlLabels: ControlLabel[]
  unknownLabels: string[]
} {
  const formatLabels: FormatLabel[] = []
  const controlLabels: ControlLabel[] = []
  const unknownLabels: string[] = []

  for (const label of labels) {
    if (!label.name) continue
    const fmt = parseFormatLabel(label.name)
    if (fmt) {
      formatLabels.push(fmt)
      continue
    }
    const ctrl = parseControlLabel(label.name)
    if (ctrl) {
      controlLabels.push(ctrl)
      continue
    }
    unknownLabels.push(label.name)
  }

  return { formatLabels, controlLabels, unknownLabels }
}

// ─── Primary label resolution ─────────────────────────────────────────────────

/**
 * Priority order for Produção mode (cards that are NOT done).
 * Returns the most important pending status.
 */
const PRODUCTION_PRIORITY: ControlLabel[] = [
  'aguardando-material',
  'gravar',
  'video',
  'design',
  'revisao',
  'outros',
  'sem-etiqueta',
]

/**
 * Priority order for Agendamento mode (cards that are NOT agendado).
 */
const SCHEDULING_PRIORITY: ControlLabel[] = [
  'finalizado',
  'aguardando-material',
  'gravar',
  'video',
  'design',
  'revisao',
  'outros',
  'sem-etiqueta',
]

export function resolvePrimaryControlLabel(
  controlLabels: ControlLabel[],
  unknownLabels: string[],
  mode: 'production' | 'scheduling'
): ControlLabel {
  if (controlLabels.length === 0 && unknownLabels.length === 0) {
    return 'sem-etiqueta'
  }

  const priority = mode === 'production' ? PRODUCTION_PRIORITY : SCHEDULING_PRIORITY

  for (const candidate of priority) {
    if (controlLabels.includes(candidate)) return candidate
  }

  // Has unknown (non-mapped) labels → "outros"
  if (unknownLabels.length > 0) return 'outros'

  return 'sem-etiqueta'
}

// ─── Done checks ─────────────────────────────────────────────────────────────

/** In production mode, a card is "done" if it has Finalizado, Agendado, OR Postar manualmente */
export function isDoneProduction(controlLabels: ControlLabel[]): boolean {
  return (
    controlLabels.includes('finalizado') ||
    controlLabels.includes('agendado') ||
    controlLabels.includes('postar-manualmente')
  )
}

/** In scheduling mode, a card is "done" only if it has Agendado */
export function isDoneScheduling(controlLabels: ControlLabel[]): boolean {
  return controlLabels.includes('agendado')
}
